import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, Select, InputLabel, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Alert, Button, Stack, Tooltip, Tabs, Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";
import {
  Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale,
  Tooltip as ChartTooltip, Legend as ChartLegend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  PendingRegistrationsPanel, StaffListTable, ClaimedOrdersTable, NotificationsPanel, UserDetailDialog,
} from "./AdminExtras";
import { sanitizeNameInput, isValidName, isValidEmail } from "./validators";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, ChartTooltip, ChartLegend);

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = { navy: "#0a0e27", accent: "#637cdb", accentDark: "#3F6AC0" };

// Ordered so the mandatory co-pay always renders first, then the optional panels.
const ITEM_ORDER = ["copay", "upload_interpretation", "drug_metabolism", "opioid_risk", "pain_perception", "all_panels", "monthly_ai_subscription"];

const ITEM_SHORT_LABEL = {
  copay: "Co-pay",
  upload_interpretation: "Upload Interp.",
  drug_metabolism: "Drug Metab.",
  opioid_risk: "Opioid Risk",
  pain_perception: "Pain Percep.",
  all_panels: "All 3 Panels",
  monthly_ai_subscription: "Monthly AI",
};

const STATUS_COLOR = {
  completed: "success",
  sent: "warning",
  pending: "warning",
};

function glassCardSx(isDark) {
  return {
    borderRadius: "16px", overflow: "hidden",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
    bgcolor: isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)",
    backdropFilter: "blur(20px) saturate(140%)",
    WebkitBackdropFilter: "blur(20px) saturate(140%)",
    boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
  };
}

function tableHeadSx(isDark) {
  return {
    "& th": {
      fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11,
      color: isDark ? "#94a3b8" : "#64748b",
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
    },
  };
}

function tableRowSx(isDark) {
  return { "& td": { borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(10,14,39,0.05)"}` } };
}

function StatTile({ label, value, isDark, hint }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: "14px",
      bgcolor: isDark ? "rgba(17,24,39,0.55)" : "rgba(255,255,255,0.65)",
      backdropFilter: "blur(16px) saturate(140%)",
      WebkitBackdropFilter: "blur(16px) saturate(140%)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
    }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.05em",
        color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function PaymentChips({ requestsByItem, isDark }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {ITEM_ORDER.map((key) => {
        const pr = requestsByItem[key];
        if (!pr) {
          return (
            <Tooltip key={key} title={`${ITEM_SHORT_LABEL[key]} — not requested`}>
              <Chip
                size="small" label={ITEM_SHORT_LABEL[key]} variant="outlined"
                sx={{ opacity: 0.4, fontSize: 11 }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip key={key} title={`${ITEM_SHORT_LABEL[key]} — ${pr.status}${pr.requested_at ? ` (requested ${new Date(pr.requested_at).toLocaleDateString()})` : ""}`}>
            <Chip
              size="small"
              label={ITEM_SHORT_LABEL[key]}
              color={STATUS_COLOR[pr.status] || "default"}
              sx={{ fontSize: 11 }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function TelehealthStaffPanel({ admin }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [role, setRole] = useState("logistics");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [npi, setNpi] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [staffRefreshKey, setStaffRefreshKey] = useState(0);

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const headers = { Authorization: `Bearer ${admin?.token}` };
      const res = await axios.post(`${API_BASE}/admin/telehealth-staff`, {
        role, first_name: firstName, last_name: lastName, email,
        ...(role === "telehealth_clinician" ? { npi } : {}),
      }, { headers });
      setMessage(`Account created for ${res.data.email} (${res.data.role === "logistics" ? "Logistics" : "Telehealth Clinician"}) — login credentials emailed.`);
      setFirstName(""); setLastName(""); setEmail(""); setNpi("");
      setStaffRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create the staff account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
    <StaffListTable admin={admin} isDark={isDark} refreshKey={staffRefreshKey} />
    <Box sx={{ ...glassCardSx(isDark), maxWidth: 520, p: 3.5 }}>
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        Logistics and Telehealth Clinician accounts are standalone — they sign in at their own dashboards
        (<code>/logistics-login</code> and <code>/telehealth-review-login</code>), not here. Create their
        credentials below; they'll get an emailed temporary password and must change it on first login.
      </Alert>
      {message && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel>Role</InputLabel>
          <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
            <MenuItem value="logistics">Logistics (kit shipping)</MenuItem>
            <MenuItem value="telehealth_clinician">Telehealth Clinician (review queue)</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={2}>
          <TextField size="small" label="First name" fullWidth value={firstName} onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))} />
          <TextField size="small" label="Last name" fullWidth value={lastName} onChange={(e) => setLastName(sanitizeNameInput(e.target.value))} />
        </Stack>
        <TextField size="small" label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
        {role === "telehealth_clinician" && (
          <TextField
            size="small" label="NPI" fullWidth value={npi}
            onChange={(e) => setNpi(e.target.value.replace(/\D/g, ""))}
            helperText="Required — used to key this clinician's report folder, same as a registered lab clinician's NPI."
          />
        )}
        <Button variant="contained" disabled={submitting || !isValidName(firstName) || !isValidName(lastName) || !isValidEmail(email) || (role === "telehealth_clinician" && !npi.trim())}
          onClick={handleCreate}
          sx={{ textTransform: "none", alignSelf: "flex-start", px: 3, borderRadius: "10px", background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)` }}>
          {submitting ? "Creating…" : "Create Account & Send Credentials"}
        </Button>
      </Stack>
    </Box>
    </Box>
  );
}

const ORDER_FUNNEL_LABELS = { pending_payment: "Pending payment", paid: "Paid — awaiting ship", shipped: "Shipped — awaiting return", received: "Received" };
const REVIEW_FUNNEL_LABELS = { not_ready: "No report yet", pending_review: "Awaiting clinician", claimed: "In review", reviewed: "Reviewed & signed" };
const REPORT_FUNNEL_LABELS = { pending_upload: "Awaiting drug-metabolism upload", delivered: "Report delivered" };
const ACTIVITY_LABEL = { reviewed: "Reviewed & signed", shipped: "Marked shipped", received: "Marked received", report_delivered: "Report delivered" };
const ACTIVITY_COLOR = { reviewed: "success", shipped: "info", received: "default", report_delivered: "success" };

function TimelineCell({ at }) {
  // Small shared cell for the full-pipeline tables below — a done
  // checkpoint shows its date in green, a not-yet-reached one shows a
  // muted dash, so the whole row reads as a left-to-right progress strip.
  return at
    ? <Chip size="small" label={new Date(at).toLocaleDateString()} color="success" variant="outlined" sx={{ fontSize: 11 }} />
    : <Typography component="span" sx={{ color: "#94a3b8", fontSize: 12.5 }}>—</Typography>;
}

function TelehealthAuditPanel({ admin }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const headers = { Authorization: `Bearer ${admin?.token}` };
      const res = await axios.get(`${API_BASE}/admin/telehealth-audit`, { headers });
      setAudit(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load the telehealth audit.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: BRAND.accent }} />;
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!audit) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {Object.entries(audit.order_funnel).map(([k, v]) => (
          <StatTile
            key={k} label={ORDER_FUNNEL_LABELS[k] || k} value={v} isDark={isDark}
            hint={k === "pending_payment" && audit.pending_payment_detail
              ? `${audit.pending_payment_detail.distinct_parents} distinct famil${audit.pending_payment_detail.distinct_parents === 1 ? "y" : "ies"}${audit.pending_payment_detail.stale_count > 0 ? ` · ${audit.pending_payment_detail.stale_count} abandoned 48h+` : ""}`
              : undefined}
          />
        ))}
        <StatTile label="Total orders" value={audit.total_orders} isDark={isDark} />
      </Box>
      {/* This number is a raw document count — every abandoned/retried
          checkout leaves its own permanent order, and nothing expires
          stale ones, so "pending payment" here can look inflated relative
          to how many families are actually waiting. The hint above and
          this note make that explicit instead of leaving it ambiguous. */}
      {audit.pending_payment_detail?.stale_count > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {audit.pending_payment_detail.stale_count} of the {audit.pending_payment_detail.total_orders} pending-payment
          order{audit.pending_payment_detail.total_orders === 1 ? "" : "s"} {audit.pending_payment_detail.stale_count === 1 ? "is" : "are"} older
          than 48 hours — likely abandoned checkouts, not active orders waiting on payment.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {Object.entries(audit.review_funnel).map(([k, v]) => (
          <StatTile key={k} label={REVIEW_FUNNEL_LABELS[k] || k} value={v} isDark={isDark} />
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {Object.entries(audit.report_funnel || {}).map(([k, v]) => (
          <StatTile key={k} label={REPORT_FUNNEL_LABELS[k] || k} value={v} isDark={isDark} />
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        <Box sx={{ ...glassCardSx(isDark), p: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1.5 }}>
            Order Pipeline
          </Typography>
          <Box sx={{ height: 220, display: "flex", justifyContent: "center" }}>
            <Doughnut
              data={{
                labels: Object.keys(audit.order_funnel).map((k) => ORDER_FUNNEL_LABELS[k] || k),
                datasets: [{
                  data: Object.values(audit.order_funnel),
                  backgroundColor: ["#f59e0b", "#637cdb", "#3F6AC0", "#16a34a"],
                  borderColor: isDark ? "#111827" : "#ffffff",
                  borderWidth: 2,
                }],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "bottom", labels: { color: isDark ? "#cbd5e1" : "#334155", boxWidth: 12, font: { size: 11 } } },
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ ...glassCardSx(isDark), p: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1.5 }}>
            Review Pipeline
          </Typography>
          <Box sx={{ height: 220 }}>
            <Bar
              data={{
                labels: Object.keys(audit.review_funnel).map((k) => REVIEW_FUNNEL_LABELS[k] || k),
                datasets: [{
                  data: Object.values(audit.review_funnel),
                  backgroundColor: "#637cdb",
                  borderRadius: 6,
                  maxBarThickness: 48,
                }],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  x: { ticks: { color: isDark ? "#cbd5e1" : "#334155", font: { size: 11 } }, grid: { display: false } },
                  y: { beginAtZero: true, ticks: { color: isDark ? "#64748b" : "#94a3b8", precision: 0 }, grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)" } },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
            Clinician Review Activity
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadSx(isDark)}>
                <TableCell>Clinician</TableCell>
                <TableCell align="right">Reviewed</TableCell>
                <TableCell>Last signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.clinicians.map((c, i) => (
                <TableRow key={i} hover sx={tableRowSx(isDark)}>
                  <TableCell>Dr. {c.name}</TableCell>
                  <TableCell align="right">{c.reviewed_count}</TableCell>
                  <TableCell>{c.last_signed_at ? new Date(c.last_signed_at).toLocaleString() : "—"}</TableCell>
                </TableRow>
              ))}
              {audit.clinicians.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: "#94a3b8" }}>No reviews yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
            Logistics Activity
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadSx(isDark)}>
                <TableCell>Staff</TableCell>
                <TableCell align="right">Shipped</TableCell>
                <TableCell align="right">Received</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.logistics_staff.map((s, i) => (
                <TableRow key={i} hover sx={tableRowSx(isDark)}>
                  <TableCell>{s.name}</TableCell>
                  <TableCell align="right">{s.shipped_count}</TableCell>
                  <TableCell align="right">{s.received_count}</TableCell>
                </TableRow>
              ))}
              {audit.logistics_staff.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: "#94a3b8" }}>No shipments yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Recent Activity
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>When</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>Child</TableCell>
              <TableCell>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit.recent_activity.map((a, i) => (
              <TableRow key={i} hover sx={tableRowSx(isDark)}>
                <TableCell>{new Date(a.at).toLocaleString()}</TableCell>
                <TableCell><Chip size="small" label={ACTIVITY_LABEL[a.action] || a.action} color={ACTIVITY_COLOR[a.action] || "default"} /></TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{a.customer_id}</TableCell>
                <TableCell>{a.child}</TableCell>
                <TableCell>{a.by}</TableCell>
              </TableRow>
            ))}
            {audit.recent_activity.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "#94a3b8" }}>No activity yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ClaimedOrdersTable admin={admin} isDark={isDark} claimedOrders={audit.claimed_orders} onChanged={load} />

      {/* Per-order audit trail — "Recent Activity" above is capped at 50
          and mixes reviews/shipments/receipts together, and the rollup
          tables only show aggregate counts per clinician/staffer. Neither
          answers "which clinician signed customer X's order" or "who
          shipped/received customer Y" directly — these two do. */}
      <TextField
        size="small" label="Search by customer ID or child name"
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 1 }}>
        <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
            Signed Orders — Who Signed What
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadSx(isDark)}>
                <TableCell>Customer ID</TableCell>
                <TableCell>Child</TableCell>
                <TableCell>Clinician</TableCell>
                <TableCell>Signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(audit.signed_orders || [])
                .filter((s) => !search.trim() || `${s.customer_id} ${s.child_name}`.toLowerCase().includes(search.trim().toLowerCase()))
                .map((s, i) => (
                <TableRow key={i} hover sx={tableRowSx(isDark)}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{s.customer_id}</TableCell>
                  <TableCell>{s.child_name}</TableCell>
                  <TableCell>Dr. {s.clinician_name}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{s.signed_at ? new Date(s.signed_at).toLocaleString() : "—"}</TableCell>
                </TableRow>
              ))}
              {(!audit.signed_orders || audit.signed_orders.length === 0) && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "#94a3b8" }}>No signed orders yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
          <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
            Shipping Orders — Who Shipped What
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadSx(isDark)}>
                <TableCell>Customer ID</TableCell>
                <TableCell>Child</TableCell>
                <TableCell>Shipped By</TableCell>
                <TableCell>Received By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(audit.shipping_orders || [])
                .filter((s) => !search.trim() || `${s.customer_id} ${s.child_name}`.toLowerCase().includes(search.trim().toLowerCase()))
                .map((s, i) => (
                <TableRow key={i} hover sx={tableRowSx(isDark)}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{s.customer_id}</TableCell>
                  <TableCell>{s.child_name}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{s.shipped_by ? `${s.shipped_by} — ${new Date(s.shipped_at).toLocaleDateString()}` : "—"}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{s.received_by ? `${s.received_by} — ${new Date(s.received_at).toLocaleDateString()}` : "—"}</TableCell>
                </TableRow>
              ))}
              {(!audit.shipping_orders || audit.shipping_orders.length === 0) && (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "#94a3b8" }}>No shipments yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* The one table that answers "where exactly is this order stuck" —
          every checkpoint for every order, side by side, instead of having
          to cross-reference the four tables above by hand. */}
      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto", mt: 3 }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Full Pipeline — Every Order, Every Checkpoint
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Customer ID</TableCell>
              <TableCell>Child</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Kit Shipped</TableCell>
              <TableCell>Kit Received</TableCell>
              <TableCell>Clinician Reviewed</TableCell>
              <TableCell>Report Delivered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(audit.full_timeline || [])
              .filter((t) => !search.trim() || `${t.customer_id} ${t.child_name}`.toLowerCase().includes(search.trim().toLowerCase()))
              .map((t, i) => (
              <TableRow key={i} hover sx={tableRowSx(isDark)}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{t.customer_id}</TableCell>
                <TableCell>{t.child_name}</TableCell>
                <TableCell><TimelineCell at={t.payment_received_at} /></TableCell>
                <TableCell><TimelineCell at={t.kit_shipped_at} /></TableCell>
                <TableCell><TimelineCell at={t.kit_received_at} /></TableCell>
                <TableCell><TimelineCell at={t.clinician_reviewed_at} /></TableCell>
                <TableCell><TimelineCell at={t.report_delivered_at} /></TableCell>
              </TableRow>
            ))}
            {(!audit.full_timeline || audit.full_timeline.length === 0) && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "#94a3b8" }}>No orders yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function OtherLabAuditPanel({ admin }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${admin?.token}` };
        const res = await axios.get(`${API_BASE}/admin/other-lab-audit`, { headers });
        setAudit(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load the Other-Labs PGx audit.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: BRAND.accent }} />;
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!audit) return null;

  const ORDER_LABELS = { pending_payment: "Pending payment", paid: "Paid" };
  const PIPELINE_LABELS = { sent_to_lab: "Sent to Proove lab folder", delivered: "Converted report delivered" };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {Object.entries(audit.order_funnel).map(([k, v]) => (
          <StatTile key={k} label={ORDER_LABELS[k] || k} value={v} isDark={isDark} />
        ))}
        {Object.entries(audit.pipeline_funnel).map(([k, v]) => (
          <StatTile key={k} label={PIPELINE_LABELS[k] || k} value={v} isDark={isDark} />
        ))}
        <StatTile label="Total orders" value={audit.total_orders} isDark={isDark} />
      </Box>

      <TextField
        size="small" label="Search by customer ID, patient, or child"
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />

      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Other-Labs PGx Conversion — Every Order, Every Checkpoint
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Customer ID</TableCell>
              <TableCell>Account Holder</TableCell>
              {/* 2026-09-27 addition — an order can now be for the account
                  holder directly OR their child (is_for_child, set at
                  checkout). This column is the only way to tell which,
                  since customer_id's kid/pt prefix alone isn't something
                  staff should have to decode by eye. */}
              <TableCell>For</TableCell>
              <TableCell>Payment Successful</TableCell>
              <TableCell>Sent to Proove Lab Folder</TableCell>
              <TableCell>Converted Report Delivered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(audit.full_timeline || [])
              .filter((t) => !search.trim() || `${t.customer_id} ${t.patient} ${t.child_name || ""}`.toLowerCase().includes(search.trim().toLowerCase()))
              .map((t, i) => (
              <TableRow key={i} hover sx={tableRowSx(isDark)}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{t.customer_id}</TableCell>
                <TableCell>{t.patient}</TableCell>
                <TableCell>
                  {t.is_for_child
                    ? <Chip size="small" label={`Child: ${t.child_name || "unnamed"}`} sx={{ fontSize: 11 }} />
                    : <Chip size="small" variant="outlined" label="Self" sx={{ fontSize: 11 }} />}
                </TableCell>
                <TableCell><TimelineCell at={t.payment_received_at} /></TableCell>
                <TableCell><TimelineCell at={t.sent_to_lab_at} /></TableCell>
                <TableCell><TimelineCell at={t.delivered_at} /></TableCell>
              </TableRow>
            ))}
            {(!audit.full_timeline || audit.full_timeline.length === 0) && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "#94a3b8" }}>No orders yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const PIF_STATUS_COLOR = { pending_payment: "warning", completed: "success" };
const PIF_STATUS_LABEL = { pending_payment: "Pending payment", completed: "Paid" };
const PIF_SOURCE_LABEL = { standalone_card: "Standalone donation", telehealth_extra: "Bundled with telehealth order" };

function PayItForwardPanel({ admin }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${admin?.token}` };
        const res = await axios.get(`${API_BASE}/admin/pay-it-forward`, { headers });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load Pay It Forward purchases.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <CircularProgress size={24} sx={{ color: BRAND.accent }} />;
  if (error) return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <StatTile label="Total purchases" value={data.totals.purchases} isDark={isDark} />
        <StatTile label="Kits sponsored (paid)" value={data.totals.kits_purchased} isDark={isDark} />
        <StatTile label="Kits remaining" value={data.totals.kits_remaining} isDark={isDark} hint="Redemption isn't built yet — always equals kits sponsored for now" />
      </Box>

      <TextField
        size="small" label="Search code, purchaser, or email"
        value={search} onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
      />

      <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
        <Typography sx={{ px: 2, pt: 2, pb: 1, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
          Purchases
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadSx(isDark)}>
              <TableCell>Code</TableCell>
              <TableCell>Purchaser</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Qty Purchased</TableCell>
              <TableCell align="right">Qty Remaining</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Purchased</TableCell>
              <TableCell>Stripe Session</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.purchases
              .filter((p) => !search.trim() || `${p.code} ${p.purchaser_name} ${p.purchaser_email}`.toLowerCase().includes(search.trim().toLowerCase()))
              .map((p) => (
              <TableRow key={p._id} hover sx={tableRowSx(isDark)}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{p.code}</TableCell>
                <TableCell>{p.purchaser_name}</TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{p.purchaser_email}</TableCell>
                <TableCell align="right">{p.quantity_purchased}</TableCell>
                <TableCell align="right">{p.quantity_remaining}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{PIF_SOURCE_LABEL[p.source] || p.source}</TableCell>
                <TableCell>
                  <Chip size="small" label={PIF_STATUS_LABEL[p.status] || p.status} color={PIF_STATUS_COLOR[p.status] || "default"} />
                </TableCell>
                <TableCell sx={{ fontSize: 12.5 }}>{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
                  {p.stripe_checkout_session_id || "—"}
                </TableCell>
              </TableRow>
            ))}
            {data.purchases.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94a3b8" }}>No purchases yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AdminDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [itemFilter, setItemFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUserId, setSelectedUserId] = useState(null);

  const admin = JSON.parse(localStorage.getItem("admin") || "null");

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${admin?.token}` };
        const [usersRes, paymentsRes] = await Promise.all([
          axios.get(`${API_BASE}/admin/users`, { headers }),
          axios.get(`${API_BASE}/admin/payment-requests`, { headers }),
        ]);
        setUsers(usersRes.data.users || []);
        setPaymentRequests(paymentsRes.data.payment_requests || []);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin");
          navigate("/admin-login", { replace: true });
          return;
        }
        setError(err.response?.data?.detail || "Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin-login", { replace: true });
  };

  // Join payment_requests onto users by email (admin/users has no user_id,
  // admin/payment-requests carries user_email — email is unique per account).
  const requestsByUserEmail = useMemo(() => {
    const map = {};
    for (const pr of paymentRequests) {
      const key = pr.user_email || "";
      if (!map[key]) map[key] = {};
      map[key][pr.item_key] = pr;
    }
    return map;
  }, [paymentRequests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;

      const userRequests = requestsByUserEmail[u.email] || {};

      if (itemFilter !== "all" || statusFilter !== "all") {
        // If an item filter is chosen, only that item's status is checked;
        // otherwise check whether ANY item matches the status filter.
        const keysToCheck = itemFilter !== "all" ? [itemFilter] : ITEM_ORDER;
        const matches = keysToCheck.some((key) => {
          const pr = userRequests[key];
          if (statusFilter === "all") return Boolean(pr);
          if (statusFilter === "not_requested") return !pr;
          return pr?.status === statusFilter;
        });
        if (!matches) return false;
      }

      if (q) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, itemFilter, statusFilter, requestsByUserEmail]);

  const stats = useMemo(() => {
    const patients = users.filter((u) => u.role === "patient");
    const clinicians = users.filter((u) => u.role === "clinician");
    const completed = paymentRequests.filter((pr) => pr.status === "completed").length;
    const pending = paymentRequests.filter((pr) => pr.status !== "completed").length;
    return {
      total: users.length,
      patients: patients.length,
      clinicians: clinicians.length,
      completed,
      pending,
    };
  }, [users, paymentRequests]);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, position: "relative", overflow: "hidden" }}>
      {/* Animated background blobs — same treatment as the main app's login/glass pages */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-12%", left: "-6%", size: 500, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.06)", delay: 0 },
          { top: "45%", right: "-10%", size: 440, color: isDark ? "rgba(99,124,219,0.07)" : "rgba(99,124,219,0.1)", delay: 1.2 },
          { top: "80%", left: "30%", size: 320, color: isDark ? "rgba(15,18,51,0.12)" : "rgba(10,14,39,0.04)", delay: 0.6 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(90px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 9 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      <Box sx={{ position: "relative", zIndex: 10, px: { xs: 2, md: 5 }, py: 4 }}>
        <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, ease: "easeOut" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", bgcolor: BRAND.navy, borderRadius: "8px", px: 1.25, py: 0.75, mb: 1.5 }}>
                <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto", display: "block" }} />
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.accent, mb: 0.5 }}>
                ADMIN
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
                User & Payments Dashboard
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <Button variant="outlined" onClick={handleLogout} sx={{ textTransform: "none", borderRadius: "10px" }}>
                Log out
              </Button>
            </Stack>
          </Box>
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, delay: 0.05, ease: "easeOut" }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <StatTile label="Total users" value={stats.total} isDark={isDark} />
            <StatTile label="Patients" value={stats.patients} isDark={isDark} />
            <StatTile label="Clinicians" value={stats.clinicians} isDark={isDark} />
            <StatTile label="Items paid" value={stats.completed} isDark={isDark} />
            <StatTile label="Items pending" value={stats.pending} isDark={isDark} />
          </Box>
        </motion.div>

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Users & Payments" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Telehealth Staff" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Telehealth Audit" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Other-Labs PGx Audit" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Pay It Forward" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab label="Notifications" sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>

        {tab === 0 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <PendingRegistrationsPanel admin={admin} isDark={isDark} />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <TextField
                size="small" label="Search name, email, phone"
                value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <FormControl size="small" sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel>Role</InputLabel>
                <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <MenuItem value="all">All roles</MenuItem>
                  <MenuItem value="patient">Patient</MenuItem>
                  <MenuItem value="clinician">Clinician</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel>Payment item</InputLabel>
                <Select label="Payment item" value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
                  <MenuItem value="all">All items</MenuItem>
                  {ITEM_ORDER.map((key) => (
                    <MenuItem key={key} value={key}>{ITEM_SHORT_LABEL[key]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="sent">Sent / Pending</MenuItem>
                  <MenuItem value="not_requested">Not requested</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer sx={{ ...glassCardSx(isDark), overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx(isDark)}>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Payment status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((u, i) => (
                    <TableRow key={u.email || i} hover onClick={() => u.id && setSelectedUserId(u.id)}
                      sx={{ ...tableRowSx(isDark), cursor: u.id ? "pointer" : "default" }}>
                      <TableCell>{u.name || "—"}</TableCell>
                      <TableCell>{u.email || "—"}</TableCell>
                      <TableCell>{u.phone || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={u.role} color={u.role === "clinician" ? "info" : "default"} />
                      </TableCell>
                      <TableCell>{u.role === "patient" ? (u.date_of_birth || "—") : "—"}</TableCell>
                      <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>
                        {u.role === "patient"
                          ? <PaymentChips requestsByItem={requestsByUserEmail[u.email] || {}} isDark={isDark} />
                          : "—"}
                        {u.is_active === false && <Chip size="small" label="Deactivated" sx={{ ml: 1 }} />}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                        No users match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        )}

        {tab === 1 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TelehealthStaffPanel admin={admin} />
          </motion.div>
        )}
        {tab === 2 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <TelehealthAuditPanel admin={admin} />
          </motion.div>
        )}
        {tab === 3 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <OtherLabAuditPanel admin={admin} />
          </motion.div>
        )}
        {tab === 4 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <PayItForwardPanel admin={admin} />
          </motion.div>
        )}
        {tab === 5 && (
          <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <NotificationsPanel admin={admin} isDark={isDark} />
          </motion.div>
        )}
      </Box>

      <UserDetailDialog
        open={Boolean(selectedUserId)} onClose={() => setSelectedUserId(null)}
        userId={selectedUserId} admin={admin} isDark={isDark}
        onChanged={() => { /* user list will show fresh data on next tab visit */ }}
      />
    </Box>
  );
}

export default AdminDashboard;
